<template>
  <v-select v-bind="$attrs"
            class="closd-select"
            :class="{
              'closd-select--small': small,
            }"
            :menu-props="{ contentClass: 'closd-select__menu' }"
            :label="selectLabel"
            :items="items"
            :hide-details="hideDetails"
            :variant="variant"
            :disabled="disabled"
            density="compact"
            item-props.color="border-info-bold"
            color="text-info"
  >
    <!-- Pass down all the slots received to VSelect to benefit from
         all its non-scoped own slots
    -->
    <template v-for="slotName in slotNames" :key="slotName" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </v-select>
</template>

<script>

/**
 * All Vuetify props for Select component are usable.
 * @see https://vuetifyjs.com/en/api/v-select/#props
 */
export default {
  name: 'AppSelect',
  inheritAttrs: false,
  props: {
    /**
     * Array of select options
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * Displayed select label
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Disable input's label
     */
    noLabel: {
      type: Boolean,
      default: false,
    },
    /**
     * Hide details space under input
     */
    hideDetails: {
      default: 'auto',
    },
    /**
     * Applies a distinct style to the component.
     */
    variant: {
      type: String,
      default: 'outlined',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    selectLabel () {
      if (this.noLabel) {
        return null
      }
      return this.label || this.$t('common.AppSelect.selectOption')
    },
    slotNames () {
      return Object.keys(this.$slots)
    },
  },
}
</script>

<style scoped lang="scss">

.closd-select {
  :deep(.v-select__menu-icon svg) {
    // Fix for Safari not rendering the icon otherwise.
    width: 10px;
  }

  :deep(.v-field) {
    --outline-color: rgb(var(--v-theme-border-secondary));
    --outline-width: 1px;
    --text-color: rgb(var(--v-theme-text-secondary));
    --background-color: rgb(var(--v-theme-surface-primary));
    --v-disabled-opacity: 1;
    --placeholder-color: rgb(var(--v-theme-text-placeholder));
    color: var(--text-color);
    background-color: var(--background-color);

    &:not(.v-field--disabled):hover {
      --outline-color: rgb(var(--v-theme-border-secondary-hover));
    }

    &:not(.v-field--disabled):focus-within {
      --outline-color: rgb(var(--v-theme-border-info-bold));
      --outline-width: 2px;
    }

    &.v-field--disabled {
      --text-color: rgb(var(--v-theme-text-disabled));
      --background-color: rgb(var(--v-theme-surface-disabled));
      --outline-color: rgb(var(--v-theme-border-disabled));
      --placeholder-color: rgb(var(--v-theme-text-disabled));
    }

    &.v-field--error {
      --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
    }
  }

  :deep(.v-field__input) {
    opacity: 1;
  }

  :deep(.v-field__input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end), {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field--active .v-field__outline__notch::before) {
    opacity: 0;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  :deep(.v-field__append-inner i) {
    color: rgb(var(--v-theme-icon-secondary));
  }

  :deep(.v-label) {
    color: rgb(var(--v-theme-text-primary));
  }
}

.closd-select.closd-select--small {
  min-width: 56px;
  :deep(.v-field__input) {
    min-height: 28px;
    padding-top: 4px;
  }

  :deep(.v-field--active .v-text-field__suffix) {
    padding-top: 2px;
    padding-bottom: 1px;
    min-height: 28px;
  }
  :deep(.v-field__append-inner) {
    padding-bottom: 3px;
  }
}
</style>
